<template lang="pug">
  .make-an-entry-activity
    .make-an-entry-content
      BField
        .level.is-mobile
          .level-left.todays-steps-level-left
            .level-item.todays-steps-input-item
              BField(
                label="Today's Steps"
                custom-class="has-text-grey"
                :type="{ 'is-danger': errors.has('steps') }"
              )
                template(slot="label")
                  | Steps
                BTooltip(
                  :active="connectedDevice"
                  animated
                  position="is-right"
                  :label="'Last Sync: ' + lastSync"
                )
                  BInput.todays-steps-input(
                    :placeholder="entryItem.todaysSteps || '0'"
                    v-model="steps"
                    name="steps"
                    :readonly="!coreProgramMeta.manualEntry || connectedDevice"
                    type="number"
                    v-validate="'numeric'"
                    data-vv-as="Steps"
                  )

      BField(
        label="Convert an Activity To Steps"
        custom-class="has-text-grey"
        v-if="coreProgramMeta.converter"
      )
        .is-flex.is-flex-wrap-wrap
          .is-flex.mr-2
            BField
              BTooltip(
                :active="activities.length < 1 && activitiesLoaded"
                label="Hmm, there are no activities to choose from. Please contact your admin."
                multilined
                position="is-top"
                size="is-small"
                animated
              )
                BAutocomplete.activities-select(
                  :max-height="140"
                  id="activities"
                  :data="filteredActivities"
                  v-model="optionName"
                  @select="val => (option = val)"
                  field="name"
                  placeholder="Select Activity"
                  :loading="!activitiesLoaded"
                  :open-on-focus="true"
                  :clearable="true"
                )
                  template(slot-scope="props")
                    .is-flex
                      figure.image.is-16x16.mr-2.is-align-self-center
                        img(:src="props.option.icon")
                      span.activities-name.is-size-7 {{ props.option.name }}
                  template(slot="empty") No results found
            .m-1 for
            BField(
              :type="{ 'is-danger': errors.has('metric') }"
            )
              BInput.activity-metric-amount(
                v-model="metricAmount"
                v-validate="'numeric'"
                data-vv-as="Metric Amount"
                name="metric"
                type="number"
              )
            .m-1 {{ conversionMetric }}
          BField.convert-button
            WtButton(
              size="small"
              outlined
              color="grey"
              @click.native="recordActivity"
              :disabled="errors.any() || !option"
            ) CONVERT

      .is-size-7.has-text-danger(v-if="errors.any()")
        ul
          li(v-for="error in errors.all()") {{ error }}
      .is-size-7.has-text-danger(v-if="showErrorNote") {{ errorNote }}
      .converted-activities-container
        Transition(name="expand")
          div(v-if="activitiesData.length > 0")
            .box.has-background-grey-lighter.converted-activities-list-box
              .converted-activities-list
                .is-size-7.has-text-weight-semibold Converted Activities:
                ul
                  li(:key="index" v-for="(item, index) in activitiesData")
                    .level.is-mobile(v-if="!item.delete")
                      .level-left
                        .level-item.activity-info
                          .is-size-7.has-text-weight-semibold
                            p.has-text-grey-dark {{ `${item.text}` }}
                            p.has-text-grey {{ `• ${item.steps.toLocaleString()} steps` }}
                      .level-right
                        .level-item
                          a.delete-activity-button.has-text-danger(
                            href="#"
                            @click.prevent="deleteActivity(index)"
                          )
                            BIcon(icon="window-close" size="is-small")

            .box.converted-steps-total.has-outline
              p.is-size-7.has-text-weight-semibold
                | Today's Steps + Activities:
              p.is-size-6.steps-total-text {{ totalSteps.toLocaleString() }} total steps!

    .level.is-mobile.program-settings-form-controls
      .level-left
      .level-right
        .level-item
          WtButton(
            is-text
            color="white"
            style="text-decoration:none"
            size="medium"
            @click.native="reset"
            :disabled="disableReset"
          ) Reset
        .level-item
          WtButton(
            @click.native="saveEntry"
            :loading="savingSteps"
            :disabled="errors.any() || disableSave || showErrorNote"
          ) SAVE
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import MakeAnEntryFooter from "@@/Members/MakeAnEntry/MakeAnEntryFooter";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";

export default {
  name: "MakeAnEntryActivity",

  components: { WtTitle, WtButton, MakeAnEntryFooter },

  mixins: [dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    fetchAction: {
      type: Function,
      default: () => {},
      required: true
    },

    entryItem: {
      type: Object,
      default: () => {},
      required: true
    },

    entryDate: {
      type: String,
      default: "",
      required: true
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      savingSteps: false,
      metricAmount: "15",
      steps: null,
      convertedSteps: "",
      option: null,
      optionName: "",
      activitiesData: [],
      showErrorNote: false,
      errorNote: ""
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),
    ...mapGetters("members", [
      "member",
      "activities",
      "activitiesLoaded",
      "activitiesLoading",
      "journals"
    ]),

    connectedDevice() {
      return (
        (this._.has(this.member, "externalDevice.deviceName") &&
          this.member.externalDevice.deviceName !== "") ||
        this.member.deviceApi === "googlefit" ||
        this.member.deviceApi === "healthconnect" ||
        this.member.deviceApi === "healthkit" ||
        this.member.deviceApi === "fitbit" ||
        this.member.deviceApi === "garmin" ||
        this.member.deviceApi === "samsunghealth"
      );
    },

    lastSync() {
      const lastSync = this._.get(
        this.member,
        "externalDevice.deviceLastContact",
        ""
      );

      return lastSync
        ? this.date(lastSync, "DATE_TIME_FORMAT").formatted
        : "Hmm, sorry, sync time not found.";
    },

    conversionMetric() {
      if (this.option !== null) {
        // below condition is to shorten "minutes" to "mins."
        // the conversion metrics other than "minutes" we have
        // are "km" or "miles", so for now this works
        return this.option.conversionMetric === "minutes"
          ? "mins."
          : this.option.conversionMetric;
      }
      return "units";
    },

    totalConvertedSteps() {
      return this.activitiesData
        .filter(activity => !activity.delete)
        .map(activity => +activity.steps)
        .reduce((total, steps) => total + steps, 0);
    },

    totalSteps() {
      let totalSteps = this.totalConvertedSteps + +this.steps;

      return totalSteps;
    },

    appendActivities() {
      return this._.isEmpty(this.entryItem)
        ? this.totalConvertedSteps > 0 // storing entry
        : this.entryItem.todaysActivities !== this.totalConvertedSteps; // updating entry
    },

    suggestedActivities() {
      const lastEntryActivities = this._.get(
        this.journals,
        "data[0].activities",
        []
      );

      return lastEntryActivities.reduce((acc, curr) => {
        const option = this.activities.find(item => item.id === curr.metricId);

        if (option && option.name) {
          return [option, ...acc];
        }

        return acc;
      }, []);
    },

    appendSteps() {
      if (!this._.isEmpty(this.entryItem)) {
        return (
          // either it's changed or it might have been changed and changed back to what it was
          this.entryItem.steps !== this.steps || this.formData.has("visibility")
        );
      }
      // either it's changed or it might have been changed and changed back to what it was
      return this.steps > 0 || this.formData.has("visibility");
    },

    filteredActivities() {
      return [...this.suggestedActivities, ...this.activities].filter(
        option => {
          return (
            option.name
              .toString()
              .toLowerCase()
              .indexOf(this.optionName.toLowerCase()) >= 0
          );
        }
      );
    }
  },

  watch: {
    steps() {
      if (this.appendSteps && this.validateMaxStepsAndMins()) {
        this.formData.set("steps", this.steps);
        this.$emit("formDataUpdated", true);
      }
    },

    metricAmount() {
      this.validateMaxStepsAndMins();
    },

    totalConvertedSteps() {
      if (this.appendActivities) {
        this.formData.set("activities", this.totalConvertedSteps);
        this.$emit("formDataUpdated", true);

        this.activitiesData.map((activity, index) => {
          // 'updating' (new = 0, delete = 0)
          // 'deleting an existing' (new = 0, delete = 1)
          // 'new' (new = 1, delete = 0)
          //  note, we don't want to send (new = 1, delete = 1)
          //       and we don't want to send not-updated data (new = 2, delete = 0)
          if (activity.new + activity.delete <= 1) {
            this.formData.set(
              `activityData[${index}][metricId]`,
              activity.metricId
            );
            this.formData.set(`activityData[${index}][date]`, activity.date);
            this.formData.set(
              `activityData[${index}][metricAmount]`,
              activity.metricAmount
            );
            this.formData.set(`activityData[${index}][steps]`, activity.steps);
            this.formData.set(
              `activityData[${index}][delete]`,
              activity.delete
            );
          }
        });
      }
    },

    totalSteps() {
      this.$emit("totalSteps", this.totalSteps);
    }
  },

  mounted() {
    if (
      !this.activitiesLoaded &&
      !this.activitiesLoading &&
      this.coreProgramMeta.converter
    ) {
      this.fetchActivities(this.$auth.user().programId);
    }

    if (!this._.isEmpty(this.entryItem)) {
      this.steps = this.entryItem.todaysSteps;
      this.activitiesData = this._.cloneDeep(this.entryItem.activities);
      this.activitiesData.map(activity => {
        // new = 0 means updating
        // new = 1 means new
        // new = 2 means not new and not updated
        this.$set(activity, "new", 2);
        this.$set(activity, "delete", 0);
      });
    }
  },

  methods: {
    ...mapActions("members", [
      "updateJournal",
      "storeJournal",
      "fetchActivities"
    ]),

    saveEntry() {
      this.savingSteps = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal);
    },

    reset() {
      const items = ["steps", "activities"];
      items.forEach(el => {
        if (this.formData.has(el)) this.formData.delete(el);
      });
      this.steps = this.entryItem.todaysSteps;
      this.activitiesData = this._.cloneDeep(this.entryItem.activities);
      this.metricAmount = "15";
      this.optionName = "";
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    },

    recordActivity() {
      if (this.validateMaxStepsAndMins()) {
        this.convertedSteps = this.option.conversionRate * this.metricAmount;
        const activityData = {
          metricId: this.option.id,
          name: this.option.name,
          metricAmount: this.metricAmount,
          steps: this.convertedSteps,
          text: `${this.metricAmount} ${this.option.conversionMetric} of ${this.option.name}`,
          delete: 0,
          new: 1
        };
        let activityAlreadyRecorded = this._.find(this.activitiesData, {
          metricId: activityData.metricId
        });
        // confusing i know :(
        // need to take into account this.deleteActivity for handling 'optimisic' deletions etc.
        if (this._.isEmpty(activityAlreadyRecorded)) {
          this.activitiesData = [...this.activitiesData, activityData];
        } else if (
          !this._.isEmpty(activityAlreadyRecorded) &&
          activityAlreadyRecorded.new === 0 &&
          this._.get(activityAlreadyRecorded, "delete", false) === 1
        ) {
          activityAlreadyRecorded.metricAmount = +activityData.metricAmount;
          activityAlreadyRecorded.steps = +activityData.steps;
          activityAlreadyRecorded.text = `${activityAlreadyRecorded.metricAmount} ${this.option.conversionMetric} of ${this.option.name}`;
          activityAlreadyRecorded.new = 0;
          activityAlreadyRecorded.delete = 0;
        } else if (
          !this._.isEmpty(activityAlreadyRecorded) &&
          activityAlreadyRecorded.new === 1
        ) {
          activityAlreadyRecorded.metricAmount =
            +activityAlreadyRecorded.metricAmount + +activityData.metricAmount;
          activityAlreadyRecorded.steps =
            +activityAlreadyRecorded.steps + +activityData.steps;
          activityAlreadyRecorded.text = `${activityAlreadyRecorded.metricAmount} ${this.option.conversionMetric} of ${this.option.name}`;
          activityAlreadyRecorded.new = 1;
        } else if (
          !this._.isEmpty(activityAlreadyRecorded) &&
          (activityAlreadyRecorded.new === 2 ||
            activityAlreadyRecorded.new === 0)
        ) {
          activityAlreadyRecorded.metricAmount =
            +activityAlreadyRecorded.metricAmount + +activityData.metricAmount;
          activityAlreadyRecorded.steps =
            +activityAlreadyRecorded.steps + +activityData.steps;
          activityAlreadyRecorded.text = `${activityAlreadyRecorded.metricAmount} ${this.option.conversionMetric} of ${this.option.name}`;
          activityAlreadyRecorded.new = 0;
        }

        // scroll to bottom of activities list after activity is recorded
        const containerElement = this.$el.querySelector(
          ".converted-activities-container"
        );
        containerElement.scrollTop = containerElement.scrollHeight;
      }
    },

    deleteActivity(index) {
      // only set new=0(update) if we are deleting existing data
      // else new=1, so data is deleted from the view and not sent back with the other data

      if (
        this.activitiesData[index].new === 2 ||
        this.activitiesData[index].new === 0
      ) {
        // note, a conditional in the layout will remove these from the view
        this.$set(this.activitiesData[index], "new", 0);
        this.$set(this.activitiesData[index], "delete", 1);
        this.$emit("formDataUpdated", true);
      }

      if (this.activitiesData[index].new === 1) {
        this.activitiesData.splice(index, 1);
        this.formData.delete(`activityData[${index}][metricId]`);
        this.formData.delete(`activityData[${index}][date]`);
        this.formData.delete(`activityData[${index}][metricAmount]`);
        this.formData.delete(`activityData[${index}][steps]`);
        this.formData.delete(`activityData[${index}][delete]`);
        // Delete formData.activities if no activityData associated with it
        let hasConvertedActivities = 0;
        for (let key of this.formData.keys()) {
          if (key.includes("activityData")) hasConvertedActivities = 1;
        }
        if (!hasConvertedActivities) {
          this.formData.delete(`activities`);
        }
        this.$emit("formDataUpdated", true);
      }
    },

    headerText() {
      if (this.entryDate === this.today().formatted) {
        return "Update Today's Activity Entry";
      }
      return `Update Activity Entry`;
    },

    validateMaxStepsAndMins() {
      const stepsToday = this.getTotalStepsToday();
      const minutesToday = this.getTotalMinutesToday();

      if (parseInt(this.steps) + stepsToday > 99999) {
        this.errorNote = "A maximum of 99,999 steps is allowed per day.";
        this.showErrorNote = true;
        return false;
      } else if (parseInt(this.metricAmount) + minutesToday > 360) {
        this.errorNote =
          "A maximum of 6 hours of conversions is allowed per day.";
        this.showErrorNote = true;
        return false;
      } else {
        if (this.showErrorNote) this.showErrorNote = false;
      }
      return true;
    },

    getTotalStepsToday() {
      let totalSteps = 0;
      this.activitiesData.forEach(data => {
        totalSteps = totalSteps + data.steps;
      });
      return totalSteps;
    },

    getTotalMinutesToday() {
      let totalMins = 0;
      this.activitiesData.forEach(data => {
        totalMins = totalMins + +data.metricAmount;
      });

      return totalMins;
    }
  }
};
</script>

<style lang="sass" scoped>
.make-an-entry-activity
  .convert-button
    button
      height: 2.5rem
  .converted-activities-container
    .expand-enter-active
      transition: height .3s, opacity .3s
    .expand-enter
      height: 0
      opacity: 0
    .box
      border-radius: $radius
      box-shadow: none
      padding: 1rem
      margin-bottom: .8rem
      &.has-outline
        border: 1px solid $grey-light
        +mobile
          min-width: 200px
          margin-right: .5rem
  .activity-metric-amount
    width: 4rem
  .activities-select
    width: 220px
    +mobile
      max-width: 40vw
.program-settings-form-controls
  margin-top: 20px
</style>
